<template>
    <el-drawer v-model="visible" direction="btt" size="225px" :before-close="handleBeforeClose" :with-header="false">
        <div class="quantity-edit-container">

            <div class="quantity-display">
                <div class="quantity-controls">
                    <el-button class="quantity-btn minus-btn" @click="decreaseQuantity" :disabled="currentQuantity <= 0"
                        size="large" circle>
                        <span class="btn-text">−</span>
                    </el-button>

                    <el-input v-model.number="currentQuantity" class="quantity-input" type="number" inputmode="numeric"
                        pattern="[0-9]*" :min="0" :max="99999" @input="handleInput" @blur="validateInput"
                        @focus="handleFocus" />

                    <el-button class="quantity-btn plus-btn" @click="increaseQuantity"
                        :disabled="currentQuantity >= 99999" size="large" circle>
                        <span class="btn-text">+</span>
                    </el-button>
                </div>
            </div>

            <div class="action-buttons">
                <el-button @click="handleCancel" class="cancel-btn">
                    キャンセル
                </el-button>
                <el-button @click="handleConfirm" class="confirm-btn">
                    更新
                </el-button>
            </div>
        </div>
    </el-drawer>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';

export default defineComponent({
    name: 'QuantityEditDrawer',
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        quantity: {
            type: Number,
            default: 0
        }
    },
    emits: ['update:modelValue', 'confirm', 'cancel'],
    setup(props, { emit }) {
        const visible = ref(false);
        const currentQuantity = ref(0);
        const originalQuantity = ref(0);
        const lastValidQuantity = ref(0);

        // Watch for visibility changes
        watch(() => props.modelValue, (newVal) => {
            visible.value = newVal;
            if (newVal) {
                currentQuantity.value = props.quantity;
                originalQuantity.value = props.quantity;
                lastValidQuantity.value = props.quantity;
            }
        });

        // Watch for quantity prop changes (only when drawer is visible)
        watch(() => props.quantity, (newVal) => {
            if (visible.value && newVal !== undefined && newVal !== null && !isNaN(newVal)) {
                currentQuantity.value = newVal;
                originalQuantity.value = newVal;
                lastValidQuantity.value = newVal;
            }
        });

        // Watch for internal visibility changes
        watch(visible, (newVal) => {
            emit('update:modelValue', newVal);
        });

        const increaseQuantity = () => {
            if (currentQuantity.value < 99999) {
                currentQuantity.value++;
                lastValidQuantity.value = currentQuantity.value;
            }
        };

        const decreaseQuantity = () => {
            if (currentQuantity.value > 0) {
                currentQuantity.value--;
                lastValidQuantity.value = currentQuantity.value;
            }
        };

        const handleInput = (value: string | number) => {
            // Allow empty string temporarily for deletion
            if (value === '' || value === null || value === undefined) {
                currentQuantity.value = 0;
                return;
            }

            // Ensure only numbers are allowed
            let numValue = typeof value === 'string' ? parseInt(value) : value;

            // Handle NaN case - preserve last valid value only if it's not from empty input
            if (isNaN(numValue)) {
                currentQuantity.value = lastValidQuantity.value;
                return;
            }

            // Enforce min/max constraints
            if (numValue < 0) {
                numValue = 0;
            } else if (numValue > 99999) {
                numValue = 99999;
            }

            currentQuantity.value = numValue;
            lastValidQuantity.value = numValue;
        };

        const handleFocus = () => {
            // Store current value as last valid when focusing
            if (!isNaN(currentQuantity.value) && currentQuantity.value !== null) {
                lastValidQuantity.value = currentQuantity.value;
            }
        };

        const validateInput = () => {
            // Additional validation on blur - ensure we have a valid number
            if (currentQuantity.value === null || currentQuantity.value === undefined || isNaN(currentQuantity.value)) {
                currentQuantity.value = 0;
            }

            if (currentQuantity.value < 0) {
                currentQuantity.value = 0;
            } else if (currentQuantity.value > 99999) {
                currentQuantity.value = 99999;
            }

            lastValidQuantity.value = currentQuantity.value;
        };

        const handleConfirm = () => {
            // Ensure we have a valid value before confirming
            validateInput();
            emit('confirm', currentQuantity.value);
            visible.value = false;
        };

        const handleCancel = () => {
            emit('cancel');
            visible.value = false;
        };

        const handleBeforeClose = () => {
            // When drawer is closed by clicking outside, preserve the current valid value
            // Don't reset to original - just close
            visible.value = false;
            return true;
        };

        return {
            visible,
            currentQuantity,
            increaseQuantity,
            decreaseQuantity,
            handleInput,
            handleFocus,
            validateInput,
            handleConfirm,
            handleCancel,
            handleBeforeClose
        };
    }
});
</script>

<style scoped>
.quantity-edit-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px;
}

.custom-header {
    text-align: center;
    padding: 10px 0 20px 0;
}

.header-title {
    font-size: 18px;
    font-weight: bold;
    color: #6b7280;
}

.quantity-display {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.quantity-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 350px;
}

.quantity-btn {
    width: 70px;
    height: 70px;
    background-color: #c8c7cc;
    border-color: #c8c7cc;
    color: #ffffff;
    font-size: 28px;
    font-weight: bold;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
}

.quantity-btn:hover:not(:disabled) {
    background-color: #9ca3af;
    border-color: #9ca3af;
}

.quantity-btn:disabled {
    background-color: #f3f4f6;
    border-color: #f3f4f6;
    color: #d1d5db;
}

.btn-text {
    font-size: 28px;
    font-weight: bold;
    line-height: 1;
}

.quantity-input {
    width: 180px;
    margin: 0 15px;
}

.quantity-input :deep(.el-input__inner) {
    font-size: 48px;
    font-weight: bold;
    color: #111827;
    text-align: center;
    border: none;
    background: transparent;
    padding: 0;
    height: auto;
    line-height: 1;
    box-shadow: none;
    outline: none;
    /* Remove default number input spinners */
    -moz-appearance: textfield;
}

/* Remove Webkit (Chrome, Safari, Edge) spinners */
.quantity-input :deep(.el-input__inner)::-webkit-outer-spin-button,
.quantity-input :deep(.el-input__inner)::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.quantity-input :deep(.el-input__inner):focus {
    border: none;
    background: transparent;
    box-shadow: none;
    outline: none;
}

.quantity-input :deep(.el-input__wrapper) {
    box-shadow: none;
    border: none;
    background: transparent;
}

.quantity-input :deep(.el-input__wrapper):focus,
.quantity-input :deep(.el-input__wrapper):hover {
    box-shadow: none;
    border: none;
    background: transparent;
}

.action-buttons {
    display: flex;
    gap: 20px;
    margin-top: 40px;
}

.action-buttons .el-button {
    flex: 1;
    height: 50px;
    font-size: 28px;
}

.cancel-btn {
    color: #f56e6e;
    border: none;
}

.confirm-btn {
    color: #215e99;
    border: none;
}
</style>
